// src/pages/Pages.tsx — product pages (PAGES-02). Edit the wording of a generated page, save it as a draft, and (owner)
// publish it. Structure, photos and prices are not edited here: prices come from the server, photos need a license
// (page_assets), and block layout changes go through a Claude session and code review.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { pages, textFields, getAt, setAt, type PageDetail, type PageDoc, type PageRow } from "../lib/pages";
import { dateTime } from "../lib/format";
import { useOperator, useToast } from "../lib/context";
import { Chip, Field, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";

const SITE = "https://greenmaestro.com";

export default function Pages() {
  const [params, setParams] = useSearchParams();
  const open = params.get("page");
  return open ? <PageEditor productKey={open} onClose={() => setParams({})} /> : <PageList onOpen={(k) => setParams({ page: k })} />;
}

function statusChip(r: { generated?: boolean; page_status: string | null; live_version: number | null; draft_version: number | null }) {
  if (r.generated === false) return <Chip tone="muted">Hand-built</Chip>;
  if (r.page_status === "live") return <Chip>Live v{r.live_version}</Chip>;
  return <Chip tone="info">Draft v{r.draft_version} · not published</Chip>;
}

function PageList({ onOpen }: { onOpen: (key: string) => void }) {
  const [rows, setRows] = useState<PageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    pages.list().then(setRows, (e: Error) => setError(e.message));
  }, []);
  return (
    <PageFrame
      title="Pages"
      lede="Product pages on greenmaestro.com. Generated pages can be edited here; hand-built pages move over one at a time."
    >
      {error && <Notice kind="error">{error}</Notice>}
      {!rows && !error && <Loading what="pages" />}
      {rows && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>Product</th><th>Page</th><th>Status</th><th>Last change</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.product_key}>
                  <td><div className="cell-main">{r.name}</div><div className="cell-sub mono">{r.product_key}</div></td>
                  <td>{r.path ? <a href={SITE + r.path} target="_blank" rel="noreferrer">{r.path}</a> : "—"}{r.slug && <div className="cell-sub">QR: /go/{r.slug}</div>}</td>
                  <td>
                    {statusChip(r)} {r.unpublished_changes && r.page_status === "live" && <Chip tone="warn">Unpublished changes</Chip>}
                  </td>
                  <td className="muted">{r.draft_updated_at ? `${dateTime(r.draft_updated_at)} · ${r.draft_updated_by ?? ""}` : "—"}</td>
                  <td>{r.generated && <button type="button" className="btn" onClick={() => onOpen(r.product_key)}>Edit wording</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageFrame>
  );
}

function PageEditor({ productKey, onClose }: { productKey: string; onClose: () => void }) {
  const me = useOperator();
  const toast = useToast();
  const canEdit = me.role === "owner" || me.role === "pricing_admin";
  const isOwner = me.role === "owner";
  const [page, setPage] = useState<PageDetail | null>(null);
  const [doc, setDoc] = useState<PageDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<"save" | "publish" | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const p = await pages.get(productKey);
      if (!p) throw new Error("This product has no generated page.");
      setPage(p);
      setDoc(p.draft);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [productKey]);
  useEffect(() => {
    void load();
  }, [load]);

  const fields = useMemo(() => (doc ? textFields(doc) : []), [doc]);
  const saved = useMemo(() => (page ? textFields(page.draft) : []), [page]);
  const edited = fields.filter((f, i) => saved[i] && f.value !== saved[i].value);
  // Wording that differs between the saved draft and the live page (what Publish would change).
  const vsLive = page && page.live ? textFields(page.draft).filter((f) => getAt(page.live, f.path) !== f.value) : [];
  const structureChanged = !!page && !!page.live && JSON.stringify(page.live.blocks.map((b) => (b as { type: string }).type)) !== JSON.stringify(page.draft.blocks.map((b) => (b as { type: string }).type));

  async function save() {
    if (!doc) return;
    setBusy("save");
    setError(null);
    try {
      const r = await pages.saveDraft(productKey, doc, reason.trim());
      toast(`✓ Draft v${r.draft_version} saved`);
      setReason("");
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    setBusy("publish");
    setError(null);
    try {
      const r = await pages.publish(productKey, reason.trim());
      const deployed = await pages.deploy();
      toast(deployed ? `✓ v${r.live_version} published — the site updates in about 2 minutes` : `✓ v${r.live_version} published — the site updates on the next deploy`);
      setReason("");
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (error && !page) return <PageFrame title="Pages"><Notice kind="error">{error}</Notice><button type="button" className="btn" onClick={onClose}>Back to pages</button></PageFrame>;
  if (!page || !doc) return <Loading what="the page" />;

  const groups: { title: string; items: { f: (typeof fields)[number]; i: number }[] }[] = [];
  fields.forEach((f, i) => {
    const title = f.label.split(" › ")[0];
    const g = groups[groups.length - 1];
    if (g && g.title === title) g.items.push({ f, i });
    else groups.push({ title, items: [{ f, i }] });
  });

  return (
    <PageFrame
      title={page.name}
      lede={<>
        {page.path && <a href={SITE + page.path} target="_blank" rel="noreferrer">{SITE.replace("https://", "")}{page.path}</a>}{" · "}
        {statusChip({ page_status: page.status, live_version: page.live_version, draft_version: page.draft_version })}{" · "}
        <Link to="/pages" onClick={(e) => { e.preventDefault(); onClose(); }}>All pages</Link>
      </>}
    >
      {!canEdit && <Notice kind="info">You can read this page's wording. Editing needs a pricing admin or the owner.</Notice>}
      <Notice kind="info">
        Wording only. Prices are filled in by the server and can't be typed here; photos, layout and links change through a Claude
        session. Saved drafts don't change the live site — publishing does.
      </Notice>

      {groups.map((g) => (
        <section key={g.title} className="editor-section">
          <SectionLabel>{g.title}</SectionLabel>
          {g.items.map(({ f, i }) => {
            const id = `pf-${i}`;
            const changed = saved[i] && f.value !== saved[i].value;
            const label = f.label.split(" › ").slice(1).join(" › ") || "text";
            return (
              <Field key={id} htmlFor={id} label={label + (changed ? " • edited" : "")}>
                {f.long ? (
                  <textarea id={id} rows={3} value={f.value} disabled={!canEdit} onChange={(e) => setDoc(setAt(doc, f.path, e.target.value))} />
                ) : (
                  <input id={id} type="text" value={f.value} disabled={!canEdit} onChange={(e) => setDoc(setAt(doc, f.path, e.target.value))} />
                )}
              </Field>
            );
          })}
        </section>
      ))}

      <section className="editor-section card">
        <SectionLabel>Save and publish</SectionLabel>
        {error && <Notice kind="error">{error}</Notice>}
        <p className="hint">
          {edited.length ? `${edited.length} unsaved edit${edited.length > 1 ? "s" : ""}.` : "No unsaved edits."}{" "}
          {page.live
            ? vsLive.length || structureChanged
              ? `The saved draft differs from the live page in ${vsLive.length} place${vsLive.length === 1 ? "" : "s"}${structureChanged ? " and in its sections" : ""}.`
              : "The saved draft matches the live page."
            : "This page has never been published from here."}
        </p>
        {vsLive.length > 0 && (
          <ul className="plain-list">
            {vsLive.slice(0, 12).map((f) => (
              <li key={f.path.join(".")}>
                <span className="muted">{f.label}:</span> <s>{String(getAt(page.live, f.path) ?? "—")}</s> → {f.value}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <Field htmlFor="pf-reason" label="Why (saved in the change log)">
            <input id="pf-reason" type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Shorter FAQ answer about branches" />
          </Field>
        )}
        <div className="row-actions">
          {canEdit && (
            <button type="button" className="btn" disabled={busy !== null || !edited.length || reason.trim().length < 5} onClick={() => void save()}>
              {busy === "save" ? "Saving…" : "Save draft"}
            </button>
          )}
          {isOwner && (
            <button
              type="button"
              className="btn primary"
              disabled={busy !== null || edited.length > 0 || reason.trim().length < 5 || (page.status === "live" && page.live_version === page.draft_version)}
              title={edited.length ? "Save the draft first" : undefined}
              onClick={() => void publish()}
            >
              {busy === "publish" ? "Publishing…" : `Publish draft v${page.draft_version}`}
            </button>
          )}
        </div>
        {isOwner && <p className="hint">Publishing puts the saved draft on {page.path}. Every image it uses must have an approved license.</p>}
      </section>

      <section className="editor-section">
        <SectionLabel>History</SectionLabel>
        <ul className="plain-list">
          {page.log.map((l, i) => (
            <li key={i}><span className="mono">{dateTime(l.at)}</span> · {l.action.replace("_", " ")} · {l.actor}{l.reason ? ` — ${l.reason}` : ""}</li>
          ))}
        </ul>
      </section>
    </PageFrame>
  );
}
